import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { formatTime, getDate } from "@miplatform/utils";
import { Box } from "@mui/material";

const getTime = () => {
  const now = getDate();
  return formatTime(now, DATE_TIME_FORMATS.FORMAT_12_HOUR_SHORT);
};

export const CurrentTimeIndicator = ({
  hourHeight,
  lineWidth,
}: ICurrentTimeIndicatorProps) => {
  const [currentTime, setCurrentTime] = useState(getTime());

  const updateTime = () => {
    const time = getTime();
    if (time !== currentTime) {
      setCurrentTime(time);
    }
  };

  const calculatePositionTop = (time: moment.Moment) => {
    const hours = time.hours();
    const minutes = time.minutes();
    const totalHours = hours + minutes / 60;
    return totalHours * hourHeight;
  };

  const topPosition = useMemo(
    () => calculatePositionTop(moment()),
    [hourHeight, currentTime]
  );

  const { classes } = useStyles({ topPosition, lineWidth });

  useEffect(() => {
    const interval = setInterval(() => {
      updateTime();
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Box className={classes.timeIndicatorContainer}>
      <Box className={classes.line} />
      <Box className={classes.timeSquare}>{currentTime}</Box>
    </Box>
  );
};

const useStyles = makeStyles<{ topPosition: number; lineWidth?: number }>()(
  (theme, { topPosition, lineWidth }) => ({
    timeIndicatorContainer: {
      position: "absolute",
      left: 0,
      width: lineWidth ? `${lineWidth}px` : "100%",
      top: topPosition,
      pointerEvents: "none",
    },
    line: {
      position: "absolute",
      left: 0,
      right: 0,
      height: 1,
      backgroundColor: theme.palette.error.main,
    },
    timeSquare: {
      position: "sticky",
      left: 0,
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      padding: theme.spacing(0, 0.5, 0.5),
      fontSize: 10,
      width: "fit-content",
      zIndex: 1,
    },
  })
);

export interface ICurrentTimeIndicatorProps {
  hourHeight: number;
  lineWidth?: number;
}
